import {
  bulletList,
  button,
  columnStyle,
  createPageTemplateRegistry,
  definePageTemplate,
  document,
  header,
  image,
  optinForm,
  row,
  section,
  text,
  video,
} from './pageTemplateEngine'

const ASSETS = Object.freeze({
  bodybuilding: '/template-media/bodybuilding-course.webp',
  marketingEbook: '/template-media/marketing-ebook-cutout.png',
  yogaWorkshop: '/template-media/yoga-workshop.webp',
  gardeningStore: '/template-media/gardening-store.webp',
  businessCoach: '/template-media/business-coach-community.webp',
  dogTrainingSuccess: '/template-media/dog-training-success.webp',
  golfEbook: '/template-media/golf-ebook-cutout.png',
  creatorVslPoster: '/template-media/creator-vsl-poster.webp',
  creatorVslVideo: '/template-media/creator-vsl-preview.webm',
})

const navigationSection = ({ brand, links, cta, backgroundColor = '#FFFFFF', textColor = '#18181B', buttonColor = '#18181B' }) => section([
  row([
    columnStyle(24, { height: 92, contentJustify: 'center' }),
    columnStyle(52, { height: 92, contentJustify: 'center', contentAlign: 'center' }),
    columnStyle(24, { height: 92, contentJustify: 'center', contentAlign: 'end' }),
  ], [
    header(0, brand, { level: 'h3', size: 24, color: textColor, height: 44 }),
    text(1, links, { size: 14, weight: 700, color: textColor, height: 40, align: 'center', textAlign: 'center' }),
    button(2, cta, { backgroundColor: buttonColor, buttonWidth: 72, height: 58, align: 'right', fontSize: 14 }),
  ], { height: 92, paddingX: 72, paddingY: 16, gutter: 20 }),
], { height: 92, backgroundColor })

const featureGridSection = ({ eyebrow, heading, items, accent = '#EC4899', backgroundColor = '#FFFFFF', headingColor = '#18181B', textColor = '#52525B' }) => section([
  row([
    columnStyle(100, { height: 144, contentJustify: 'center', contentAlign: 'center' }),
  ], [
    text(0, eyebrow, { size: 13, weight: 800, color: accent, height: 28, align: 'center', textAlign: 'center' }),
    header(0, heading, { size: 40, color: headingColor, height: 70, align: 'center', textAlign: 'center' }),
  ], { height: 144, paddingX: 88, paddingY: 20 }),
  row(items.map(() => columnStyle(100 / items.length, { height: 220, paddingX: 24, paddingY: 28, contentJustify: 'center' })), items.flatMap((item, columnIndex) => [
    header(columnIndex, item.title, { size: 26, color: headingColor, height: 50 }),
    text(columnIndex, item.copy, { size: 16, color: textColor, height: 106 }),
  ]), { height: 220, paddingX: 72, paddingY: 22, gutter: 24 }),
], { height: 364, backgroundColor })

const storySection = ({ imageUrl, eyebrow, heading, copy, imageOnRight = false, backgroundColor = '#F8FAFC', accent = '#EC4899', headingColor = '#18181B', textColor = '#52525B', cta = '' }) => {
  const imageColumn = imageOnRight ? 1 : 0
  const copyColumn = imageOnRight ? 0 : 1
  const elements = [
    image(imageColumn, imageUrl, { imageWidth: 100, height: 390, radius: 24, shadow: 22 }),
    text(copyColumn, eyebrow, { size: 13, weight: 800, color: accent, height: 30 }),
    header(copyColumn, heading, { size: 42, color: headingColor, height: 116 }),
    text(copyColumn, copy, { size: 17, color: textColor, height: 122 }),
  ]
  if (cta) elements.push(button(copyColumn, cta, { backgroundColor: accent, buttonWidth: 32, height: 68 }))
  return section([
    row([
      columnStyle(50, { height: 460, paddingX: 22, paddingY: 34, contentJustify: 'center' }),
      columnStyle(50, { height: 460, paddingX: 36, paddingY: 40, contentJustify: 'center' }),
    ], elements, { height: 460, paddingX: 72, paddingY: 28, gutter: 44 }),
  ], { height: 460, backgroundColor })
}

const testimonialSection = ({ quote, author, detail, backgroundColor = '#18181B', textColor = '#FFFFFF', accent = '#F9A8D4' }) => section([
  row([
    columnStyle(100, { height: 300, paddingX: 60, paddingY: 46, contentJustify: 'center', contentAlign: 'center' }),
  ], [
    text(0, 'CUSTOMER STORY', { size: 13, weight: 800, color: accent, height: 28, align: 'center', textAlign: 'center' }),
    header(0, `“${quote}”`, { size: 38, color: textColor, height: 130, width: 82, align: 'center', textAlign: 'center', lineHeight: 1.2 }),
    text(0, `${author}  •  ${detail}`, { size: 15, weight: 700, color: accent, height: 34, align: 'center', textAlign: 'center' }),
  ], { height: 300, paddingX: 90, paddingY: 34 }),
], { height: 300, backgroundColor })

const ctaSection = ({ eyebrow, heading, copy, buttonLabel, backgroundColor = '#EC4899', textColor = '#FFFFFF', buttonColor = '#18181B' }) => section([
  row([
    columnStyle(100, { height: 330, paddingX: 54, paddingY: 48, contentJustify: 'center', contentAlign: 'center' }),
  ], [
    text(0, eyebrow, { size: 13, weight: 800, color: textColor, height: 28, align: 'center', textAlign: 'center' }),
    header(0, heading, { size: 46, color: textColor, height: 90, width: 82, align: 'center', textAlign: 'center' }),
    text(0, copy, { size: 17, color: textColor, height: 62, width: 72, align: 'center', textAlign: 'center' }),
    button(0, buttonLabel, { backgroundColor: buttonColor, buttonWidth: 28, height: 68, align: 'center' }),
  ], { height: 330, paddingX: 76, paddingY: 34 }),
], { height: 330, backgroundColor })

const footerSection = ({ brand, copy, backgroundColor = '#09090B', textColor = '#D4D4D8' }) => section([
  row([
    columnStyle(36, { height: 130, contentJustify: 'center' }),
    columnStyle(64, { height: 130, contentJustify: 'center', contentAlign: 'end' }),
  ], [
    header(0, brand, { level: 'h3', size: 24, color: '#FFFFFF', height: 42 }),
    text(0, copy, { size: 13, color: textColor, height: 38 }),
    text(1, 'About  •  Resources  •  Contact  •  Privacy', { size: 14, weight: 700, color: textColor, height: 40, align: 'right', textAlign: 'right' }),
  ], { height: 130, paddingX: 72, paddingY: 24, gutter: 30 }),
], { height: 130, backgroundColor })

const buildBlank = () => document('Blank Canvas', 'Start with a clean, editable section.', [
  section([], { height: 120, backgroundColor: '#FFFFFF' }),
])

const buildCourseLaunch = () => document(
  'Bodybuilding Coaching Course',
  'A complete strength-coaching course page for serious lifters.',
  [
    navigationSection({ brand: 'Forge Strength', links: 'Program  •  Results  •  Coaching', cta: 'View the program', backgroundColor: '#FFF7F7', buttonColor: '#18181B' }),
    section([
      row([
        columnStyle(54, { height: 520, paddingX: 42, paddingY: 38, contentJustify: 'center', backgroundColor: 'rgba(255,255,255,0.90)' }),
        columnStyle(46, { height: 520 }),
      ], [
        text(0, '12-WEEK HYPERTROPHY SYSTEM', { size: 13, weight: 800, color: '#DC2626', height: 30 }),
        header(0, 'Build measurable muscle with a plan that earns every rep.', { level: 'h1', size: 58, color: '#111827', height: 190 }),
        text(0, 'Progressive programming, practical nutrition, and weekly coaching for lifters ready to train with purpose instead of guessing.', { size: 18, color: '#4B5563', height: 108 }),
        button(0, 'Start the program', { backgroundColor: '#DC2626', buttonWidth: 32, height: 72 }),
      ], { height: 520, paddingX: 72, paddingY: 38, gutter: 28 }),
    ], { height: 520, backgroundColor: '#FFF7FB', backgroundImageUrl: ASSETS.bodybuilding, backgroundPosition: 'center' }),
    section([
      row([
        columnStyle(33.33, { height: 190, paddingX: 18, paddingY: 24, contentJustify: 'center' }),
        columnStyle(33.33, { height: 190, paddingX: 18, paddingY: 24, contentJustify: 'center' }),
        columnStyle(33.34, { height: 190, paddingX: 18, paddingY: 24, contentJustify: 'center' }),
      ], [
        header(0, '01', { size: 28, color: '#EC4899', height: 42 }),
        text(0, 'Assess your baseline\nSet honest starting numbers for strength, volume, and recovery.', { size: 16, color: '#3F3F46', height: 92 }),
        header(1, '02', { size: 28, color: '#EC4899', height: 42 }),
        text(1, 'Train progressively\nFollow a structured split that makes every week build on the last.', { size: 16, color: '#3F3F46', height: 92 }),
        header(2, '03', { size: 28, color: '#EC4899', height: 42 }),
        text(2, 'Recover deliberately\nUse nutrition, sleep, and deloads to turn hard work into growth.', { size: 16, color: '#3F3F46', height: 92 }),
      ], { height: 190, paddingX: 72, paddingY: 20, gutter: 20 }),
    ], { height: 190 }),
    storySection({
      imageUrl: ASSETS.bodybuilding,
      eyebrow: 'BUILT FOR LONG-TERM PROGRESS',
      heading: 'Programming, nutrition, and feedback in one focused system.',
      copy: 'Every training block includes clear targets, demonstration notes, recovery guidance, and coach check-ins so members always know what to do next.',
      imageOnRight: true,
      cta: 'Explore the program',
      backgroundColor: '#F3F4F6',
    }),
    featureGridSection({
      eyebrow: 'WHAT ATHLETES RECEIVE',
      heading: 'Everything needed to train hard and recover smarter.',
      items: [
        { title: 'Progressive blocks', copy: 'Twelve weeks of intelligently sequenced training with clear load and rep targets.' },
        { title: 'Nutrition framework', copy: 'Simple calorie, protein, and meal-building guidance that supports lean growth.' },
        { title: 'Coach feedback', copy: 'Weekly form reviews and adjustments that keep effort pointed in the right direction.' },
      ],
    }),
    testimonialSection({ quote: 'For the first time, every hard session had a clear reason—and my numbers finally moved again.', author: 'Marcus T.', detail: 'Forge athlete', accent: '#FCA5A5' }),
    ctaSection({ eyebrow: 'THE NEXT BLOCK STARTS MONDAY', heading: 'Train with a system built for measurable growth.', copy: 'Join the next coaching group and make the next twelve weeks your strongest yet.', buttonLabel: 'Start the program', backgroundColor: '#DC2626' }),
    footerSection({ brand: 'Forge Strength', copy: 'Evidence-led bodybuilding coaching for serious lifters.' }),
  ],
)

const buildLeadMagnet = () => document(
  'Marketing Growth Guide',
  'A focused digital-marketing lead-capture page for guides, reports, and free resources.',
  [
    navigationSection({ brand: 'Signal Studio', links: 'Insights  •  Playbooks  •  Newsletter', cta: 'Get the guide', backgroundColor: '#111827', textColor: '#FFFFFF', buttonColor: '#EC4899' }),
    section([
      row([
        columnStyle(58, { height: 510, paddingX: 34, paddingY: 42, contentJustify: 'center' }),
        columnStyle(42, { height: 510, paddingX: 20, paddingY: 30, contentJustify: 'center' }),
      ], [
        text(0, 'FREE 24-PAGE FIELD GUIDE', { size: 13, weight: 800, color: '#F9A8D4', height: 30 }),
        header(0, 'The complete guide to building an audience that buys.', { level: 'h1', size: 54, color: '#FFFFFF', height: 184 }),
        text(0, 'Learn the content rhythms, conversion prompts, and simple metrics used by independent creators to grow without burning out.', { size: 18, color: '#CBD5E1', height: 110 }),
        button(0, 'Send me the free guide', { backgroundColor: '#EC4899', buttonWidth: 34, height: 72 }),
        image(1, ASSETS.marketingEbook, { imageWidth: 86, height: 390, radius: 0, shadow: 10 }),
      ], { height: 510, paddingX: 78, paddingY: 36, gutter: 44 }),
    ], { height: 510, backgroundColor: '#111827' }),
    section([
      row([
        columnStyle(100, { height: 180, paddingX: 24, paddingY: 28, contentJustify: 'center', contentAlign: 'center' }),
      ], [
        header(0, 'Inside the guide', { size: 34, color: '#111827', height: 54, align: 'center', textAlign: 'center' }),
        text(0, 'A repeatable content calendar  •  Five high-converting page prompts  •  A simple weekly scorecard', { size: 17, color: '#52525B', height: 54, align: 'center', textAlign: 'center' }),
      ], { height: 180, paddingX: 72, paddingY: 32 }),
    ], { height: 180, backgroundColor: '#F8FAFC' }),
    storySection({
      imageUrl: ASSETS.marketingEbook,
      eyebrow: 'BUILT FROM REAL CAMPAIGNS',
      heading: 'Use a system you can repeat every week.',
      copy: 'See how a focused message, useful content, and one clear invitation work together. Every block is editable, so you can tailor the guide to your audience and offer.',
      cta: 'See what is inside',
      backgroundColor: '#FFFFFF',
      accent: '#7C3AED',
    }),
    featureGridSection({
      eyebrow: 'THE PLAYBOOK',
      heading: 'A practical system, not more theory.',
      accent: '#7C3AED',
      backgroundColor: '#F5F3FF',
      items: [
        { title: 'Plan clearly', copy: 'Choose the message and content rhythm that fit the way you actually work.' },
        { title: 'Publish consistently', copy: 'Use a simple weekly structure that keeps quality high without draining your time.' },
        { title: 'Measure what matters', copy: 'Track the small set of signals that reveal what your audience wants next.' },
      ],
    }),
    testimonialSection({ quote: 'The weekly scorecard showed me exactly which content was bringing the right people into my business.', author: 'Jordan Ellis', detail: 'Consultant', backgroundColor: '#312E81' }),
    ctaSection({ eyebrow: 'FREE DOWNLOAD', heading: 'Turn attention into an audience you own.', copy: 'Get the complete field guide and put the first page to work this week.', buttonLabel: 'Send me the guide', backgroundColor: '#7C3AED' }),
    footerSection({ brand: 'Signal Studio', copy: 'Clear ideas for sustainable audience growth.' }),
  ],
)

const buildWebinar = () => document(
  'Yoga Live Workshop',
  'A calm, premium registration page for a live yoga workshop.',
  [
    navigationSection({ brand: 'Stillpoint Studio', links: 'Practice  •  Your guide  •  FAQs', cta: 'Reserve a mat', backgroundColor: '#1F2933', textColor: '#FFFFFF', buttonColor: '#D97757' }),
    section([
      row([
        columnStyle(52, { height: 500, paddingX: 38, paddingY: 38, contentJustify: 'center', backgroundColor: 'rgba(9,9,11,0.84)' }),
        columnStyle(48, { height: 500 }),
      ], [
        text(0, 'LIVE ONLINE PRACTICE  •  SATURDAY 9 AM ET', { size: 13, weight: 800, color: '#F4A261', height: 34 }),
        header(0, 'Move with strength. Finish with a quieter mind.', { level: 'h1', size: 56, color: '#FFFFFF', height: 176 }),
        text(0, 'Join a guided all-levels practice combining steady strength, thoughtful mobility, and breathwork you can return to all week.', { size: 18, color: '#D4D4D8', height: 118 }),
        button(0, 'Reserve my mat', { backgroundColor: '#D97757', buttonWidth: 32, height: 72 }),
      ], { height: 500, paddingX: 76, paddingY: 34, gutter: 42 }),
    ], { height: 500, backgroundColor: '#09090B', backgroundImageUrl: ASSETS.yogaWorkshop, backgroundPosition: 'center' }),
    section([
      row([
        columnStyle(33.33, { height: 150, paddingX: 20, paddingY: 24, contentJustify: 'center', contentAlign: 'center' }),
        columnStyle(33.33, { height: 150, paddingX: 20, paddingY: 24, contentJustify: 'center', contentAlign: 'center' }),
        columnStyle(33.34, { height: 150, paddingX: 20, paddingY: 24, contentJustify: 'center', contentAlign: 'center' }),
      ], [
        header(0, '90 minutes', { size: 28, color: '#FFFFFF', height: 44, align: 'center', textAlign: 'center' }),
        text(0, 'Strength, mobility, breath', { size: 15, color: '#A1A1AA', height: 38, align: 'center', textAlign: 'center' }),
        header(1, 'All levels', { size: 28, color: '#FFFFFF', height: 44, align: 'center', textAlign: 'center' }),
        text(1, 'Options for every body', { size: 15, color: '#A1A1AA', height: 38, align: 'center', textAlign: 'center' }),
        header(2, 'Replay included', { size: 28, color: '#FFFFFF', height: 44, align: 'center', textAlign: 'center' }),
        text(2, 'Watch again anytime', { size: 15, color: '#A1A1AA', height: 38, align: 'center', textAlign: 'center' }),
      ], { height: 150, paddingX: 76, paddingY: 18, gutter: 18 }),
    ], { height: 150, backgroundColor: '#18181B' }),
    storySection({
      imageUrl: ASSETS.yogaWorkshop,
      eyebrow: 'PRACTICE WITH A PRESENT GUIDE',
      heading: 'Build a sequence you can return to between classes.',
      copy: 'We will move through grounded standing work, accessible balance, longer mobility holds, and a guided closing breath practice.',
      imageOnRight: true,
      cta: 'View the practice',
      backgroundColor: '#FAFAFA',
    }),
    featureGridSection({
      eyebrow: 'THE PRACTICE',
      heading: 'Three intentional phases.',
      backgroundColor: '#09090B',
      headingColor: '#FFFFFF',
      textColor: '#A1A1AA',
      items: [
        { title: 'Arrive', copy: 'Settle the breath and prepare the joints with slow, purposeful mobility.' },
        { title: 'Build heat', copy: 'Move through a balanced strength flow with clear alignment cues and options.' },
        { title: 'Restore', copy: 'Close with longer holds and simple breathwork that downshifts the nervous system.' },
      ],
    }),
    testimonialSection({ quote: 'I felt challenged, supported, and noticeably calmer before the final pose.', author: 'Elena R.', detail: 'Workshop member', backgroundColor: '#29352E', accent: '#F4A261' }),
    ctaSection({ eyebrow: 'LIMITED LIVE SPACES', heading: 'Make room for one focused morning of movement.', copy: 'Reserve your mat and receive the replay plus a seven-day follow-along sequence.', buttonLabel: 'Reserve my mat', backgroundColor: '#D97757' }),
    footerSection({ brand: 'Stillpoint Studio', copy: 'Strong, spacious practice for everyday bodies.', backgroundColor: '#1F2933', textColor: '#D8E2DC' }),
  ],
)

const buildModernSales = () => document(
  'Gardening Product Sales',
  'A bright gardening-store sales page with an editorial hero and offer highlights.',
  [
    navigationSection({ brand: 'Root & Row', links: 'Garden kits  •  Seeds  •  Field notes', cta: 'Shop kits', backgroundColor: '#F5F1E8', textColor: '#24432E', buttonColor: '#24432E' }),
    section([
      row([
        columnStyle(48, { height: 520, paddingX: 42, paddingY: 40, contentJustify: 'center', backgroundColor: 'rgba(255,251,235,0.90)' }),
        columnStyle(52, { height: 520 }),
      ], [
        text(0, 'THE KITCHEN GARDEN COLLECTION', { size: 13, weight: 800, color: '#B45309', height: 30 }),
        header(0, 'Grow herbs and vegetables you will actually cook with.', { level: 'h1', size: 56, color: '#24432E', height: 178 }),
        text(0, 'Curated seeds, durable tools, and a week-by-week growing plan designed for patios, raised beds, and first-time gardeners.', { size: 18, color: '#536B59', height: 108 }),
        button(0, 'Shop the garden kit', { backgroundColor: '#24432E', buttonWidth: 32, height: 72 }),
      ], { height: 520, paddingX: 72, paddingY: 34, gutter: 36 }),
    ], { height: 520, backgroundColor: '#FDF2F8', backgroundImageUrl: ASSETS.gardeningStore, backgroundPosition: 'center' }),
    section([
      row([
        columnStyle(100, { height: 210, paddingX: 40, paddingY: 32, contentJustify: 'center', contentAlign: 'center' }),
      ], [
        header(0, 'Good seeds. Clear instructions. No guesswork.', { size: 36, color: '#24432E', height: 72, align: 'center', textAlign: 'center' }),
        text(0, 'Plant with confidence  •  Water with purpose  •  Harvest all season', { size: 17, color: '#617565', height: 50, align: 'center', textAlign: 'center' }),
      ], { height: 210, paddingX: 80, paddingY: 36 }),
    ], { height: 210, backgroundColor: '#FFFFFF' }),
    storySection({
      imageUrl: ASSETS.gardeningStore,
      eyebrow: 'START WITH THE RIGHT MIX',
      heading: 'A productive garden begins with a thoughtful first planting.',
      copy: 'Each kit pairs reliable varieties with a seasonal planting map, simple soil guidance, and the tools needed to keep young plants thriving.',
      cta: 'Explore the seed mix',
      backgroundColor: '#FFF7ED',
      accent: '#9F1239',
      headingColor: '#24432E',
      textColor: '#536B59',
    }),
    featureGridSection({
      eyebrow: 'WHY IT GROWS',
      heading: 'Thoughtful tools for a garden you will keep.',
      accent: '#9F1239',
      backgroundColor: '#FFFFFF',
      headingColor: '#24432E',
      textColor: '#536B59',
      items: [
        { title: 'Seasonal', copy: 'Planting calendars keep every variety aligned with local light and temperature.' },
        { title: 'Practical', copy: 'Durable tools and clear instructions make daily garden care straightforward.' },
        { title: 'Abundant', copy: 'Succession planting keeps herbs and vegetables coming long after the first harvest.' },
      ],
    }),
    testimonialSection({ quote: 'By midsummer, our small patio was giving us herbs and tomatoes faster than we could use them.', author: 'Naomi R.', detail: 'Verified gardener', backgroundColor: '#24432E', accent: '#FDE68A' }),
    ctaSection({ eyebrow: 'THE COMPLETE GARDEN KIT', heading: 'One box. A full season of useful harvests.', copy: 'Start with the complete collection and make growing food the best part of your week.', buttonLabel: 'Shop the garden kit', backgroundColor: '#3F7D44', buttonColor: '#183A1D' }),
    footerSection({ brand: 'Root & Row', copy: 'Useful gardens, thoughtfully grown.', backgroundColor: '#183A1D', textColor: '#DDE5D8' }),
  ],
)

const buildCommunity = () => document(
  'Business Coach Mastermind',
  'A premium membership landing page for a business coach, mastermind, or founder cohort.',
  [
    navigationSection({ brand: 'Founder Table', links: 'Mastermind  •  Sessions  •  Results', cta: 'Apply today', backgroundColor: '#172554', textColor: '#FFFFFF', buttonColor: '#2563EB' }),
    section([
      row([
        columnStyle(48, { height: 500 }),
        columnStyle(52, { height: 500, paddingX: 40, paddingY: 40, contentJustify: 'center', backgroundColor: 'rgba(23,37,84,0.88)' }),
      ], [
        text(1, 'A SMALL ROOM FOR SERIOUS FOUNDERS', { size: 13, weight: 800, color: '#93C5FD', height: 30 }),
        header(1, 'Make the next decision with better people around you.', { level: 'h1', size: 58, color: '#FFFFFF', height: 160 }),
        text(1, 'Join a focused coaching community for founders building healthier teams, clearer offers, and more predictable growth.', { size: 18, color: '#E2E8F0', height: 104 }),
        button(1, 'Apply to the mastermind', { backgroundColor: '#2563EB', buttonWidth: 42, height: 72 }),
      ], { height: 500, paddingX: 76, paddingY: 32, gutter: 42 }),
    ], { height: 500, backgroundColor: '#312E81', backgroundImageUrl: ASSETS.businessCoach, backgroundPosition: 'center' }),
    section([
      row([
        columnStyle(50, { height: 190, paddingX: 28, paddingY: 30, contentJustify: 'center' }),
        columnStyle(50, { height: 190, paddingX: 28, paddingY: 30, contentJustify: 'center' }),
      ], [
        header(0, 'Decide with context', { size: 30, color: '#172554', height: 50 }),
        text(0, 'Bring the real numbers, team constraints, and customer signals behind the decision.', { size: 16, color: '#52525B', height: 78 }),
        header(1, 'Execute every week', { size: 30, color: '#172554', height: 50 }),
        text(1, 'Leave each session with one committed priority, an owner, and a clear measure of progress.', { size: 16, color: '#52525B', height: 78 }),
      ], { height: 190, paddingX: 76, paddingY: 20, gutter: 34 }),
    ], { height: 190, backgroundColor: '#F5F3FF' }),
    storySection({
      imageUrl: ASSETS.businessCoach,
      eyebrow: 'COACHING WITH OPERATING RHYTHM',
      heading: 'Turn founder overwhelm into a shorter, clearer list.',
      copy: 'Use weekly decision rooms, monthly operating reviews, and private coaching to keep the company focused on the work that compounds.',
      imageOnRight: true,
      cta: 'See the mastermind rhythm',
      backgroundColor: '#FFFFFF',
      accent: '#7C3AED',
      headingColor: '#312E81',
    }),
    featureGridSection({
      eyebrow: 'YOUR MASTERMIND',
      heading: 'Perspective, accountability, and operating support.',
      accent: '#7C3AED',
      backgroundColor: '#F5F3FF',
      headingColor: '#312E81',
      items: [
        { title: 'Decision rooms', copy: 'Work through the highest-leverage decision with founders who understand the tradeoffs.' },
        { title: 'Private coaching', copy: 'Get direct support for leadership, positioning, hiring, and execution bottlenecks.' },
        { title: 'Operating library', copy: 'Use proven scorecards, meeting rhythms, and planning templates with your team.' },
      ],
    }),
    testimonialSection({ quote: 'The room helped me stop adding projects and finally commit the company to the one growth lever that mattered.', author: 'Samira Patel', detail: 'Agency founder', backgroundColor: '#172554', accent: '#93C5FD' }),
    ctaSection({ eyebrow: 'APPLY TO FOUNDER TABLE', heading: 'Build the company with a room that raises the quality of every decision.', copy: 'Meet the founders, coaching, and weekly operating rhythm behind your next stage of growth.', buttonLabel: 'Apply to join', backgroundColor: '#2563EB' }),
    footerSection({ brand: 'Founder Table', copy: 'A focused coaching room for ambitious founders.', backgroundColor: '#0F172A', textColor: '#93C5FD' }),
  ],
)

const buildThankYou = () => document(
  'Dog Training Thank You',
  'A polished pet-training confirmation page with clear next steps.',
  [
    navigationSection({ brand: 'Good Dog Method', links: 'Lessons  •  Help  •  Community', cta: 'Open lessons', backgroundColor: '#1E3A5F', textColor: '#FFFFFF', buttonColor: '#F59E0B' }),
    section([
      row([
        columnStyle(100, { height: 520, paddingX: 44, paddingY: 54, contentJustify: 'center', contentAlign: 'center' }),
      ], [
        text(0, 'YOU’RE ALL SET', { size: 13, weight: 800, color: '#F9A8D4', height: 30, align: 'center', textAlign: 'center' }),
        header(0, 'You and your dog are ready to begin.', { level: 'h1', size: 60, color: '#FFFFFF', height: 92, align: 'center', textAlign: 'center' }),
        text(0, 'Your confirmation is on its way. While you wait, open the welcome lesson and choose one calm behavior to practice first.', { size: 18, color: '#E2E8F0', height: 88, width: 68, align: 'center', textAlign: 'center' }),
        image(0, ASSETS.dogTrainingSuccess, { imageWidth: 48, height: 190, radius: 22, shadow: 20, align: 'center' }),
        button(0, 'Open the welcome lesson', { backgroundColor: '#F59E0B', buttonWidth: 30, height: 72, align: 'center' }),
      ], { height: 520, paddingX: 76, paddingY: 38 }),
    ], { height: 520, backgroundColor: '#172554' }),
    featureGridSection({
      eyebrow: 'WHAT HAPPENS NEXT',
      heading: 'Three simple ways to get started.',
      accent: '#EC4899',
      backgroundColor: '#F8FAFC',
      headingColor: '#172554',
      items: [
        { title: 'Check your inbox', copy: 'Your confirmation and account details should arrive in the next few minutes.' },
        { title: 'Choose one cue', copy: 'Start with the welcome lesson and select one useful behavior to reinforce this week.' },
        { title: 'Meet the community', copy: 'Introduce your dog, share your training goal, and get encouragement from other owners.' },
      ],
    }),
    storySection({
      imageUrl: ASSETS.dogTrainingSuccess,
      eyebrow: 'YOUR TRAINING PLAN',
      heading: 'Short lessons make calm habits easier to repeat.',
      copy: 'Find demonstrations, practice plans, progress notes, and community conversations in one place so everyone in the home trains consistently.',
      imageOnRight: true,
      cta: 'Open my lessons',
      backgroundColor: '#FFFFFF',
      accent: '#EC4899',
      headingColor: '#172554',
    }),
    ctaSection({ eyebrow: 'NEED A HAND?', heading: 'We are here to help you take the first step.', copy: 'Visit the help center or send the team a message whenever you need support.', buttonLabel: 'Visit the help center', backgroundColor: '#2563EB' }),
    footerSection({ brand: 'Good Dog Method', copy: 'Practical training for calmer homes and happier dogs.', backgroundColor: '#1E3A5F', textColor: '#BFDBFE' }),
  ],
)

const buildCreatorVsl = () => document(
  'OnlyFans Membership VSL',
  'A complete fan-facing video sales letter an OnlyFans creator can use to invite customers into a private membership.',
  [
    navigationSection({ brand: 'Lena After Dark', links: 'Preview  •  What’s inside  •  Join', cta: 'Watch preview', backgroundColor: '#07070A', textColor: '#FFFFFF', buttonColor: '#FF3B5C' }),
    section([
      row([
        columnStyle(100, { height: 790, paddingX: 54, paddingY: 44, contentJustify: 'center', contentAlign: 'center' }),
      ], [
        text(0, 'A PRIVATE INVITATION FROM LENA', { size: 13, weight: 800, color: '#FF536F', height: 30, align: 'center', textAlign: 'center' }),
        header(0, 'Come closer—there’s more waiting inside.', { level: 'h1', size: 58, color: '#FFFFFF', height: 140, width: 82, align: 'center', textAlign: 'center' }),
        text(0, 'Watch this quick preview, then step into my private membership for exclusive drops, live moments, and the side of me I save for members.', { size: 18, color: '#A1A1AA', height: 96, width: 72, align: 'center', textAlign: 'center' }),
        video(0, ASSETS.creatorVslVideo, { posterUrl: ASSETS.creatorVslPoster, height: 450, width: 72, align: 'center', radius: 18, controlColor: '#FF3B5C' }),
      ], { height: 790, paddingX: 72, paddingY: 34 }),
    ], { height: 790, backgroundColor: '#07070A' }),
    featureGridSection({
      eyebrow: 'INSIDE THE MEMBERSHIP',
      heading: 'More access. More connection. New drops every week.',
      accent: '#FF536F',
      backgroundColor: '#0F0F15',
      headingColor: '#FFFFFF',
      textColor: '#A1A1AA',
      items: [
        { title: 'Exclusive drops', copy: 'See members-only photo and video sets I do not post anywhere else.' },
        { title: 'Private live rooms', copy: 'Join relaxed livestreams, behind-the-scenes moments, and members-first reveals.' },
        { title: 'Closer connection', copy: 'Vote on upcoming themes, send requests, and be the first to hear what is next.' },
      ],
    }),
    storySection({
      imageUrl: ASSETS.creatorVslPoster,
      eyebrow: 'A NOTE FROM ME',
      heading: 'The content I save for the people who want more.',
      copy: 'I created this private space so we can go beyond the public feed. Members get the full archive, new weekly releases, casual behind-the-scenes updates, and a real say in what I create next.',
      imageOnRight: false,
      cta: 'See what members get',
      backgroundColor: '#141019',
      accent: '#FF536F',
      headingColor: '#FFFFFF',
      textColor: '#C4B5C5',
    }),
    section([
      row([
        columnStyle(50, { height: 430, paddingX: 34, paddingY: 42, contentJustify: 'center' }),
        columnStyle(50, { height: 430, paddingX: 40, paddingY: 44, contentJustify: 'center' }),
      ], [
        text(0, 'JOIN THE INNER CIRCLE', { size: 13, weight: 800, color: '#FF536F', height: 30 }),
        header(0, 'Your private pass starts here.', { size: 44, color: '#FFFFFF', height: 118 }),
        text(0, 'Unlock the full archive today, then come back each week for something new.', { size: 17, color: '#A1A1AA', height: 72 }),
        button(0, 'Join my private membership', { backgroundColor: '#FF3B5C', buttonWidth: 54, height: 70 }),
        bulletList(1, [
          'New members-only photo and video drops every week',
          'Private livestreams and behind-the-scenes updates',
          'First access to themed sets and special releases',
          'Members-only polls, requests, and direct updates',
          'Cancel anytime—stay only while you love it',
        ], { height: 250, iconColor: '#22C55E', color: '#D4D4D8', size: 16 }),
      ], { height: 430, paddingX: 92, paddingY: 34, gutter: 54, backgroundColor: '#101016' }),
    ], { height: 430, backgroundColor: '#07070A' }),
    testimonialSection({ quote: 'The membership feels personal, the weekly drops are always worth the wait, and the live rooms are my favorite part.', author: 'J.M.', detail: 'Member since launch', backgroundColor: '#101016', accent: '#FF8AA0' }),
    ctaSection({ eyebrow: 'COME INSIDE', heading: 'Ready to see the side I do not share anywhere else?', copy: 'Join now for immediate access to the archive and every new members-only release.', buttonLabel: 'Join the inner circle', backgroundColor: '#FF3B5C', buttonColor: '#111116' }),
    footerSection({ brand: 'Lena After Dark', copy: 'Private drops, live moments, and more of me—for members only.', backgroundColor: '#050507', textColor: '#71717A' }),
  ],
)

const buildGolfLeadMagnetEbook = () => document(
  'Lead Magnet EBook',
  'A complete golf ebook opt-in funnel with a real editable lead form.',
  [
    navigationSection({ brand: 'Fairway Method', links: 'The guide  •  Lessons  •  About', cta: 'Free download', backgroundColor: '#0C2F23', textColor: '#FFFFFF', buttonColor: '#D4A72C' }),
    section([
      row([
        columnStyle(48, { height: 620, paddingX: 24, paddingY: 42, contentJustify: 'center', contentAlign: 'center' }),
        columnStyle(52, { height: 620, paddingX: 38, paddingY: 40, contentJustify: 'center' }),
      ], [
        image(0, ASSETS.golfEbook, { imageWidth: 94, height: 470, radius: 0, shadow: 10, align: 'center' }),
        text(1, 'FREE GOLF STRATEGY GUIDE', { size: 13, weight: 800, color: '#D4A72C', height: 30 }),
        header(1, 'Play smarter golf before you change a single thing in your swing.', { level: 'h1', size: 52, color: '#FFFFFF', height: 150 }),
        text(1, 'The 36-page course-management guide for choosing better targets, avoiding expensive misses, and turning solid ball-striking into lower scores.', { size: 17, color: '#D1D5DB', height: 96 }),
        optinForm(1, { id: 'fairway-guide', height: 270, headline: 'Send me the free guide', description: 'Enter your details for instant access.', buttonLabel: 'Download the Fairway Blueprint', backgroundColor: '#FFFFFF', labelColor: '#374151', inputTextColor: '#111827', buttonBackgroundColor: '#D4A72C', buttonTextColor: '#172217', radius: 10 }),
      ], { height: 620, paddingX: 76, paddingY: 34, gutter: 48 }),
    ], { height: 620, backgroundColor: '#0C2F23' }),
    section([
      row([
        columnStyle(42, { height: 370, paddingX: 28, paddingY: 36, contentJustify: 'center' }),
        columnStyle(58, { height: 370, paddingX: 36, paddingY: 36, contentJustify: 'center' }),
      ], [
        header(0, 'What is inside the guide?', { size: 40, color: '#173C2E', height: 96 }),
        text(0, 'A practical decision system you can carry from the first tee to the eighteenth green.', { size: 17, color: '#52665C', height: 86 }),
        bulletList(1, [
          'A tee-shot decision tree for every hole shape',
          'The safest target when trouble guards the green',
          'Club-selection rules for wind, slope, and temperature',
          'A post-round scorecard that reveals the shots costing you most',
          'A simple practice plan built from your own round data',
        ], { height: 250, iconColor: '#2F855A', color: '#294438', size: 16 }),
      ], { height: 370, paddingX: 86, paddingY: 34, gutter: 54 }),
    ], { height: 370, backgroundColor: '#F4F1E8' }),
    storySection({
      imageUrl: ASSETS.golfEbook,
      eyebrow: 'LOWER SCORES START WITH BETTER DECISIONS',
      heading: 'Stop aiming at flags that do not deserve your attention.',
      copy: 'Use dispersion, hazards, lie, and your real carry numbers to choose targets that keep doubles off the card and make pars easier to collect.',
      imageOnRight: true,
      cta: 'Get the free guide',
      backgroundColor: '#FFFFFF',
      accent: '#B2871F',
      headingColor: '#173C2E',
      textColor: '#52665C',
    }),
    testimonialSection({ quote: 'The target system removed three penalty shots in my first round using it.', author: 'Daniel K.', detail: '12-handicap golfer', backgroundColor: '#173C2E', accent: '#E8D7A2' }),
    ctaSection({ eyebrow: 'FREE INSTANT DOWNLOAD', heading: 'Make your next round easier before you reach the first tee.', copy: 'Download the guide, print the course checklist, and bring a smarter plan to your next round.', buttonLabel: 'Download the guide', backgroundColor: '#B2871F', buttonColor: '#173C2E' }),
    footerSection({ brand: 'Fairway Method', copy: 'Course strategy for golfers who want lower scores now.', backgroundColor: '#0C2F23', textColor: '#C7D2CC' }),
  ],
)

export const corePageTemplateDefinitions = Object.freeze([
  definePageTemplate({ id: 'blank', name: 'Blank Canvas', category: 'Blank', description: 'Start from a clean editable section.', tags: ['blank'], build: buildBlank }),
  definePageTemplate({ id: 'course-launch', name: 'Bodybuilding Course', category: 'Sales', description: 'A complete bodybuilding coaching page with generated gym imagery, program structure, proof, CTA, and footer.', tags: ['bodybuilding', 'fitness', 'course', 'coaching', 'sales'], build: buildCourseLaunch }),
  definePageTemplate({ id: 'lead-magnet', name: 'Marketing Growth Guide', category: 'Lead Gen', description: 'A complete marketing guide funnel with generated product imagery, benefits, proof, CTA, and footer.', tags: ['marketing', 'lead generation', 'ebook', 'guide'], build: buildLeadMagnet }),
  definePageTemplate({ id: 'live-workshop', name: 'Yoga Live Workshop', category: 'Webinar', description: 'A complete yoga workshop page with generated studio imagery, agenda, proof, registration CTA, and footer.', tags: ['yoga', 'wellness', 'webinar', 'workshop'], build: buildWebinar }),
  definePageTemplate({ id: 'modern-product-sales', name: 'Gardening Product Sales', category: 'Ecommerce', description: 'A complete gardening storefront with generated lifestyle imagery, product benefits, proof, CTA, and footer.', tags: ['gardening', 'ecommerce', 'product', 'store'], build: buildModernSales }),
  definePageTemplate({ id: 'community-welcome', name: 'Business Coach Mastermind', category: 'Membership', description: 'A complete business coaching mastermind page with generated community imagery and a full membership funnel.', tags: ['business coach', 'mastermind', 'membership', 'community'], build: buildCommunity }),
  definePageTemplate({ id: 'thank-you', name: 'Dog Training Thank You', category: 'Confirmation', description: 'A complete pet-training confirmation experience with generated imagery and clear next steps.', tags: ['dog training', 'pets', 'confirmation', 'onboarding'], build: buildThankYou }),
  definePageTemplate({ id: 'creator-growth-vsl', name: 'VSL', category: 'Sales', description: 'A complete fan-facing OnlyFans membership video sales letter with a playable preview, benefits, offer stack, proof, CTA, and footer.', tags: ['vsl', 'video sales letter', 'onlyfans', 'membership'], build: buildCreatorVsl }),
  definePageTemplate({ id: 'golf-lead-magnet-ebook', name: 'Lead Magnet EBook', category: 'Lead Gen', description: 'A complete golf ebook funnel with generated product imagery, a functioning opt-in form, benefits, proof, CTA, and footer.', tags: ['golf', 'lead magnet', 'ebook', 'optin'], build: buildGolfLeadMagnetEbook }),
])

const templatePackContext = require.context('../pageTemplates', true, /\.template-pack\.js$/)
const additionalTemplateDefinitions = templatePackContext.keys().flatMap((key) => {
  const templatePack = templatePackContext(key)
  const definitions = templatePack.default || templatePack.templates || []
  return Array.isArray(definitions) ? definitions : [definitions]
}).filter(Boolean)

const templateRegistry = createPageTemplateRegistry([
  ...corePageTemplateDefinitions,
  ...additionalTemplateDefinitions,
], { fallbackId: 'blank' })

export const pageTemplates = templateRegistry.templates
export const buildPageTemplate = (templateId) => templateRegistry.build(templateId)
